import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";
import { AUTH } from "@/constants/testIds";
import { APPLY_LINK_PROPS } from "@/constants/links";
import { extractAxiosError } from "@/lib/api-errors";

const MerchantLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "merchant") return <Navigate to="/merchant/dashboard" replace />;
  if (user && user.role === "agent") return <Navigate to="/agent/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "merchant") {
        setError("This portal is for merchants. Use the agent console instead.");
        return;
      }
      navigate("/merchant/dashboard");
    } catch (err) {
      setError(extractAxiosError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={AUTH.merchantLoginPage} className="min-h-screen bg-cf-cream text-cf-ink cf-soft-rays">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex items-center justify-between">
        <CFLogo />
        <Link to="/" className="text-xs uppercase tracking-[0.16em] text-cf-ink-soft hover:text-cf-ink cf-link-underline">
          ← Back to site
        </Link>
      </div>

      <div className="max-w-md mx-auto px-6 mt-16">
        <span className="cf-overline">Merchant portal</span>
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mt-3">
          Welcome back. <span className="italic text-cf-gold">Sign in.</span>
        </h1>
        <p className="text-cf-ink-soft text-sm mt-3">
          Track your application, upload documents, and chat with your advisor.
        </p>

        <form
          onSubmit={submit}
          className="mt-10 bg-white rounded-3xl border border-cf-line p-8 shadow-[0_25px_60px_-30px_rgba(15,37,64,0.18)]"
        >
          <div>
            <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Email</label>
            <input
              data-testid={AUTH.merchantLoginEmail}
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="cf-input-light" placeholder="you@business.com" autoComplete="email"
            />
          </div>
          <div className="mt-5">
            <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Password</label>
            <input
              data-testid={AUTH.merchantLoginPassword}
              required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="cf-input-light" placeholder="••••••••" autoComplete="current-password"
            />
          </div>
          {error && (
            <p data-testid={AUTH.merchantLoginError} className="mt-5 text-sm text-red-600">{error}</p>
          )}
          <button
            data-testid={AUTH.merchantLoginSubmit}
            type="submit" disabled={loading}
            className="cf-btn-gold w-full justify-center mt-7 disabled:opacity-50"
          >
            {loading ? "Signing in..." : <>Sign in <ArrowRight size={16} strokeWidth={2} /></>}
          </button>
          <div className="flex items-center gap-2 text-cf-slate text-xs mt-5 justify-center">
            <ShieldCheck size={12} strokeWidth={1.8} className="text-cf-gold" />
            Encrypted session · no credit pulls
          </div>
        </form>

        <p className="text-cf-slate text-sm mt-6 text-center">
          New to Capless? <a {...APPLY_LINK_PROPS} data-testid="merchant-login-apply-link" className="text-cf-ink font-semibold cf-link-underline">Apply for funding →</a>
        </p>
      </div>
    </div>
  );
};

export default MerchantLogin;
